import React, { PropsWithChildren, useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    backgroundColor: '#162746',
    backgroundImage: 'none',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const useSidebarStyles = makeStyles({
  sidebar: {
    backgroundColor: '#162746 !important',
    backgroundImage: 'none !important',
    '& > div': {
      backgroundColor: '#162746 !important',
      backgroundImage: 'none !important',
    },
    '& [class*="MuiBox-root"]': {
      backgroundColor: '#162746 !important',
      backgroundImage: 'none !important',
    },
    '& [class*="MuiPaper-root"]': {
      backgroundColor: '#162746 !important',
      backgroundImage: 'none !important',
    },
    '& [class*="MuiList-root"], & [class*="MuiListItem-root"]': {
      backgroundColor: '#162746 !important',
      backgroundImage: 'none !important',
    },
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const SidebarWithStyles = ({ children }: PropsWithChildren<{}>) => {
  const classes = useSidebarStyles();
  return (
    <Sidebar className={classes.sidebar}>
      <ForceSidebarOpen />
      {children}
    </Sidebar>
  );
};

const ForceSidebarOpen = () => {
  const sidebarState = useSidebarOpenState() as {
    isOpen?: boolean;
    setOpen?: (open: boolean) => void;
  };

  useEffect(() => {
    if (sidebarState.isOpen === false && sidebarState.setOpen) {
      sidebarState.setOpen(true);
    }
  }, [sidebarState.isOpen, sidebarState.setOpen]);

  return null;
};

/** Reads version metadata from app-config and renders a compact label in the sidebar. */
const VersionInfo = () => {
  const config = useApi(configApiRef);
  const { isOpen } = useSidebarOpenState();
  if (!isOpen) return null;

  const imageVersion = config.getOptionalString('app.imageVersion') ?? 'dev';
  const createAppVersion = config.getOptionalString('app.createAppVersion') ?? 'unknown';

  return (
    <div
      style={{
        padding: '6px 24px 10px',
        color: 'rgba(255,255,255,0.45)',
        fontSize: '10px',
        lineHeight: '1.6',
        userSelect: 'text',
      }}
    >
      <div>v{imageVersion}</div>
      <div>create-app@{createAppVersion}</div>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <SidebarWithStyles>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="catalogx" text="Home" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        <SidebarSettings />
        <VersionInfo />
      </SidebarGroup>
    </SidebarWithStyles>
    {children}
  </SidebarPage>
);
